import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import BreadcrumbNav from "./BreadcrumbNav";
import type { MigrationService } from "../data/migrationServices";
import { toRootAssetUrl } from "../utils/assetUrl";

// Visual settings for service cards — ensure visible on white background
const DEBUG_SERVICES = false;
export type ServicePageSectionProps = {
  services: MigrationService[];
};

const easeOutCurve = [0.22, 1, 0.36, 1] as const;

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: easeOutCurve },
  },
};

const gridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.15,
    },
  },
};

const ServiceCard = ({ title, description, icon, url }: MigrationService) => {
  const navigate = useNavigate();
  return (
  <article
    className={
      `flex flex-col items-start text-left rounded-3xl group min-h-[260px] min-w-0 p-8 box-border relative overflow-visible bg-white border border-transparent transition-shadow transition-colors duration-300` +
      (DEBUG_SERVICES ? ' border-indigo-200 bg-white/5' : '') +
      ` hover:border-slate-100 hover:shadow-[0_8px_32px_rgba(15,23,42,0.06)] focus-within:border-slate-100` +
      (url ? ' cursor-pointer' : '')
    }
    tabIndex={0}
    style={{ outline: "none" }}
    onClick={url ? () => { navigate(url); } : undefined}
    role={url ? "link" : undefined}
    onMouseMove={e => {
      const card = e.currentTarget;
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      card.style.setProperty('--glow-x', `${x}px`);
      card.style.setProperty('--glow-y', `${y}px`);
      card.style.setProperty('--glow-opacity', '1');
    }}
    onMouseLeave={e => {
      const card = e.currentTarget;
      card.style.setProperty('--glow-opacity', '0');
    }}
  >
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 rounded-[28px] transition-opacity duration-300"
      style={{
        opacity: 'var(--glow-opacity, 0)',
        background:
          'radial-gradient(80px 80px at var(--glow-x, 50%) var(--glow-y, 50%), rgba(76,76,255,0.10) 0%, rgba(76,76,255,0.06) 40%, rgba(76,76,255,0) 100%)',
        zIndex: 1,
      }}
    />
    <img
      src={toRootAssetUrl(icon)}
      alt={`${title} icon`}
      className="h-14 w-14 -translate-y-[2px] object-contain mb-4 relative z-10"
      loading="eager"
      decoding="async"
    />
    <h3 className="mb-2 text-[22px] font-bold leading-[1.25] tracking-[-0.2px] text-[#111111] max-w-[340px] relative z-10">
      {title}
    </h3>
    <p className="text-[15px] font-normal leading-[1.55] text-[#555555] max-w-[340px] relative z-10">
      {description}
    </p>
  </article>
  );
};

export default function ServicePageSection({ services }: ServicePageSectionProps) {
  return (
    <section className="bg-white">
      <div className="mx-auto max-w-[1180px] pl-10 pr-6 py-[100px]">
        <h2 className="mb-2 text-[40px] font-extrabold text-[#111] leading-tight tracking-tight text-left max-w-[600px]">Our Migration Services</h2>
        {services.length === 0 ? (
          <div className="mt-12 text-sm text-slate-500">No services available at the moment.</div>
        ) : (
          <motion.div
            className="mt-12 grid grid-cols-1 gap-y-[40px] gap-x-[40px] sm:grid-cols-2 lg:grid-cols-3 justify-center"
            initial="hidden"
            animate="visible"
            variants={gridVariants}
          >
            {services.map((service) => (
              <motion.div key={service.title} variants={cardVariants} className="flex w-full">
                <ServiceCard {...service} />
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </section>
  );
}
